import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { subscriptionTypeAdminService } from '../services/subscriptionTypeAdminService'
import { PageHeader } from '../components/ui/PageHeader'
import { Button } from '../components/ui/Button'

const emptyNew = {
  subscription_code: '',
  subscription_name: '',
  total_credits: '0',
  is_active: true,
  is_default: false
}

function parseApiFieldErrors(err) {
  const errors = err?.response?.data?.errors
  if (!Array.isArray(errors)) return {}
  const next = {}
  errors.forEach((e) => {
    const key = (e.field || '').replace(/^body\./, '')
    if (key) next[key] = e.message
  })
  return next
}

function rowDraftFromRow(row) {
  return {
    subscription_name: row.subscription_name ?? '',
    total_credits: String(row.total_credits ?? 0),
    is_active: Boolean(row.is_active)
  }
}

const SubscriptionManagement = () => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [newRow, setNewRow] = useState(emptyNew)
  const [newRowErrors, setNewRowErrors] = useState({})
  const [drafts, setDrafts] = useState({})
  const [rowErrors, setRowErrors] = useState({})
  const [savingCode, setSavingCode] = useState(null)
  const [creating, setCreating] = useState(false)
  const [deletingCode, setDeletingCode] = useState(null)
  const [settingDefaultCode, setSettingDefaultCode] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const list = await subscriptionTypeAdminService.list()
      const arr = Array.isArray(list) ? list : []
      setRows(arr)
      const nextDrafts = {}
      arr.forEach((r) => {
        nextDrafts[r.subscription_code] = rowDraftFromRow(r)
      })
      setDrafts(nextDrafts)
      setRowErrors({})
    } catch {
      setRows([])
      setDrafts({})
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const defaultCode = useMemo(() => rows.find((r) => r.is_default)?.subscription_code ?? null, [rows])

  const setDraftField = (code, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [code]: {
        ...prev[code],
        [field]: value
      }
    }))
    setRowErrors((prev) => {
      const next = { ...prev }
      delete next[`${code}:${field}`]
      return next
    })
  }

  const handleSaveRow = async (code) => {
    const d = drafts[code]
    if (!d) return
    setRowErrors((prev) => {
      const next = { ...prev }
      Object.keys(next).forEach((k) => {
        if (k.startsWith(`${code}:`)) delete next[k]
      })
      return next
    })
    setSavingCode(code)
    try {
      const credits = parseInt(d.total_credits, 10)
      if (!Number.isFinite(credits) || credits < 0) {
        setRowErrors((prev) => ({
          ...prev,
          [`${code}:total_credits`]: 'Enter a valid non-negative whole number'
        }))
        return
      }
      if (!String(d.subscription_name || '').trim()) {
        setRowErrors((prev) => ({
          ...prev,
          [`${code}:subscription_name`]: 'Subscription name is required'
        }))
        return
      }
      await subscriptionTypeAdminService.update(code, {
        subscription_name: String(d.subscription_name).trim(),
        total_credits: credits,
        is_active: d.is_active
      })
      await load()
    } catch (err) {
      const parsed = parseApiFieldErrors(err)
      const mapped = {}
      Object.keys(parsed).forEach((k) => {
        mapped[`${code}:${k}`] = parsed[k]
      })
      setRowErrors((prev) => ({ ...prev, ...mapped }))
    } finally {
      setSavingCode(null)
    }
  }

  const handleSetDefault = async (code) => {
    if (defaultCode === code) return
    setSettingDefaultCode(code)
    try {
      await subscriptionTypeAdminService.update(code, { is_default: true })
      await load()
    } catch {
      // toast in service
    } finally {
      setSettingDefaultCode(null)
    }
  }

  const handleCreate = async () => {
    setNewRowErrors({})
    const credits = parseInt(newRow.total_credits, 10)
    const err = {}
    if (!String(newRow.subscription_code || '').trim()) {
      err.subscription_code = 'Subscription code is required'
    }
    if (!String(newRow.subscription_name || '').trim()) {
      err.subscription_name = 'Subscription name is required'
    }
    if (!Number.isFinite(credits) || credits < 0) {
      err.total_credits = 'Enter a valid non-negative whole number'
    }
    if (Object.keys(err).length > 0) {
      setNewRowErrors(err)
      return
    }
    setCreating(true)
    try {
      await subscriptionTypeAdminService.create({
        subscription_code: String(newRow.subscription_code).trim().toUpperCase(),
        subscription_name: String(newRow.subscription_name).trim(),
        total_credits: credits,
        is_active: Boolean(newRow.is_active),
        is_default: Boolean(newRow.is_default)
      })
      setNewRow(emptyNew)
      setNewRowErrors({})
      await load()
    } catch (e) {
      setNewRowErrors(parseApiFieldErrors(e))
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete subscription type "${code}"? Organisations using it will have subscription_code set to null.`)) {
      return
    }
    setDeletingCode(code)
    try {
      await subscriptionTypeAdminService.destroy(code)
      await load()
    } catch {
      // toast in service
    } finally {
      setDeletingCode(null)
    }
  }

  return (
    <div>
      <PageHeader
        title="Subscription management"
        subtitle="Manage subscription types, credits, and the default plan for new organisations."
        actions={
          <Button type="button" variant="secondary" onClick={() => load()} disabled={loading}>
            Refresh
          </Button>
        }
      />

      <div className="card overflow-hidden p-0">
        {loading ? (
          <div className="p-10 text-center text-gray-600">Loading subscription types…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Name</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Total credits</th>
                  <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Active</th>
                  <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Default</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Updated</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                <tr className="bg-purple-50/40">
                  <td className="px-4 py-3 align-top">
                    <input
                      type="text"
                      maxLength={10}
                      value={newRow.subscription_code}
                      onChange={(e) => {
                        setNewRow((r) => ({ ...r, subscription_code: e.target.value.toUpperCase() }))
                        setNewRowErrors((er) => {
                          const n = { ...er }
                          delete n.subscription_code
                          return n
                        })
                      }}
                      placeholder="CODE"
                      className={`w-full rounded border px-2 py-1.5 text-sm font-mono uppercase ${
                        newRowErrors.subscription_code ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {newRowErrors.subscription_code && (
                      <p className="mt-1 text-xs text-red-600">{newRowErrors.subscription_code}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <input
                      type="text"
                      maxLength={25}
                      value={newRow.subscription_name}
                      onChange={(e) => {
                        setNewRow((r) => ({ ...r, subscription_name: e.target.value }))
                        setNewRowErrors((er) => {
                          const n = { ...er }
                          delete n.subscription_name
                          return n
                        })
                      }}
                      placeholder="Display name"
                      className={`w-full min-w-[8rem] rounded border px-2 py-1.5 text-sm ${
                        newRowErrors.subscription_name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {newRowErrors.subscription_name && (
                      <p className="mt-1 text-xs text-red-600">{newRowErrors.subscription_name}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top text-right">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={newRow.total_credits}
                      onChange={(e) => {
                        setNewRow((r) => ({ ...r, total_credits: e.target.value }))
                        setNewRowErrors((er) => {
                          const n = { ...er }
                          delete n.total_credits
                          return n
                        })
                      }}
                      className={`w-28 rounded border px-2 py-1.5 text-sm text-right ${
                        newRowErrors.total_credits ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {newRowErrors.total_credits && (
                      <p className="mt-1 text-right text-xs text-red-600">{newRowErrors.total_credits}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center align-top">
                    <input
                      type="checkbox"
                      checked={newRow.is_active}
                      onChange={(e) => setNewRow((r) => ({ ...r, is_active: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-purple-600"
                    />
                  </td>
                  <td className="px-4 py-3 text-center align-top">
                    <input
                      type="checkbox"
                      checked={newRow.is_default}
                      onChange={(e) => setNewRow((r) => ({ ...r, is_default: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-purple-600"
                    />
                  </td>
                  <td className="px-4 py-3 align-top text-sm text-gray-400">—</td>
                  <td className="px-4 py-3 align-top text-right">
                    <button
                      type="button"
                      onClick={handleCreate}
                      disabled={creating}
                      className="rounded-lg bg-purple-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 disabled:opacity-50"
                    >
                      {creating ? 'Adding…' : 'Add'}
                    </button>
                  </td>
                </tr>
                {rows.map((row) => {
                  const d = drafts[row.subscription_code] || rowDraftFromRow(row)
                  const nameErr = rowErrors[`${row.subscription_code}:subscription_name`]
                  const creditsErr = rowErrors[`${row.subscription_code}:total_credits`]
                  return (
                    <tr key={row.subscription_code} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-mono font-medium text-gray-900">
                        {row.subscription_code}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <input
                          type="text"
                          maxLength={25}
                          value={d.subscription_name}
                          onChange={(e) => setDraftField(row.subscription_code, 'subscription_name', e.target.value)}
                          className={`input-field min-w-[8rem] py-1.5 ${
                            nameErr ? 'border-red-500' : ''
                          }`}
                        />
                        {nameErr && <p className="mt-1 text-xs text-red-600">{nameErr}</p>}
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={d.total_credits}
                          onChange={(e) => setDraftField(row.subscription_code, 'total_credits', e.target.value)}
                          className={`w-28 rounded border px-2 py-1.5 text-sm text-right ${
                            creditsErr ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {creditsErr && <p className="mt-1 text-right text-xs text-red-600">{creditsErr}</p>}
                      </td>
                      <td className="px-4 py-3 text-center align-top">
                        <input
                          type="checkbox"
                          checked={d.is_active}
                          onChange={(e) => setDraftField(row.subscription_code, 'is_active', e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-purple-600"
                        />
                      </td>
                      <td className="px-4 py-3 text-center align-top">
                        <input
                          type="radio"
                          name="subscription_default_pick"
                          checked={Boolean(row.is_default)}
                          onChange={() => handleSetDefault(row.subscription_code)}
                          disabled={settingDefaultCode === row.subscription_code}
                          className="h-4 w-4 border-gray-300 text-purple-600"
                          title="Default for new organisations"
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {row.updated_at
                          ? new Date(row.updated_at).toLocaleString(undefined, {
                              dateStyle: 'medium',
                              timeStyle: 'short'
                            })
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right align-top">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleSaveRow(row.subscription_code)}
                            disabled={savingCode === row.subscription_code}
                            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                          >
                            {savingCode === row.subscription_code ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(row.subscription_code)}
                            disabled={deletingCode === row.subscription_code}
                            className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
                          >
                            {deletingCode === row.subscription_code ? '…' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {rows.length === 0 && (
              <div className="px-6 py-8 text-center text-sm text-gray-500">
                No subscription types yet. Add one using the first row.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default SubscriptionManagement
